/**
 * What the signed-in person may create / manage through CATEGORY roles (context grants), on top of the global
 * role permissions that come with the user.
 *
 * A "Course Creator" on one category only (given to them, to a cohort they're in, or to their department) may
 * create courses there — the API resolves course:manage against the course's category — but every screen used
 * to read only `user.roles[].permissions`, so the Explore "Create" button, the admin menu and the course form
 * never offered it. GET /v1/context-roles/me/capabilities answers it; nothing is cached server-side, and here
 * it is refetched on focus / after 30 s so a new or removed grant shows without signing in again.
 */
import { useQuery } from '@tanstack/react-query';
import axiosInstance from '@/api/axiosInstance';
import { CONTEXT_ROLE_ENDPOINTS } from '@/api/endpoints/context-role.endpoints';
import { useAuth } from '@/contexts/shared/AuthContext';

export interface ScopedCategory { id: number; name: string; parent_id: number | null; depth: number }

export interface ScopedCapabilities {
  course_manage_global: boolean;
  /** null = every category (tenant-wide holder); else only these. */
  course_category_ids: number[] | null;
  course_categories: ScopedCategory[];
  can_create_course: boolean;
  program_manage_global: boolean;
  program_category_ids: number[] | null;
  program_categories: ScopedCategory[];
  can_create_program: boolean;
  can_create_community: boolean;
  /** Some of the above comes ONLY from category grants. */
  scoped_only: boolean;
}

export const SCOPED_CAPABILITIES_KEY = (userId: unknown) => ['me', 'scoped-capabilities', userId] as const;

export const useScopedCapabilities = (enabled = true) => {
  const { user, isAuthenticated } = useAuth();
  const userId = (user as { id?: number } | null)?.id;
  return useQuery({
    queryKey: SCOPED_CAPABILITIES_KEY(userId),
    queryFn: async () => (await axiosInstance.get<ScopedCapabilities>(CONTEXT_ROLE_ENDPOINTS.MY_CAPABILITIES)).data,
    enabled: enabled && isAuthenticated && !!userId,
    staleTime: 30 * 1000,
    retry: false,
  });
};

/** Keep only the categories (of a picker) where courses may be created; all of them for a tenant-wide holder.
 *  Categories the general list doesn't carry (e.g. hidden from learners) are added from the capability answer. */
export const restrictToCategories = <T extends { id: number | string }>(
  all: T[],
  ids: number[] | null | undefined,
  extra: ScopedCategory[] | undefined,
  make: (c: ScopedCategory) => T,
): T[] => {
  if (ids == null) return all;
  const allowed = new Set(ids);
  const kept = all.filter((c) => allowed.has(Number(c.id)));
  const have = new Set(kept.map((c) => Number(c.id)));
  return [...kept, ...(extra ?? []).filter((c) => !have.has(c.id)).map(make)];
};
